import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ApiCallsHelper } from 'app/modules/common/helpers/ApiCallsHelper';
import { CurrenyService } from 'app/modules/common/services/curreny.service';
import { PageLoaderService } from 'app/modules/common/services/page-loader.service';
import { SharedService } from 'app/modules/common/services/shared-service';
import { Actuation } from 'app/modules/common/types/actuation.type';
import { DropDownOption } from 'app/modules/common/types/dropdown-option.type';
import { ActuationService } from '../../services/actuation.service';

export type EditActuationDialogData = {
    actuationId: number;
};

@Component({
    selector: 'app-edit-actuation-type-dialog',
    templateUrl: './edit-actuation-type-dialog.component.html',
    styleUrls: ['./edit-actuation-type-dialog.component.scss'],
    standalone: false
})
export class EditActuationTypeDialogComponent implements OnInit {
    actionTypes: DropDownOption<number>[] = [];
    form: UntypedFormGroup;

    constructor(
        private actuationService: ActuationService,
        private pageLoader: PageLoaderService,
        private sharedService: SharedService,
        private dialogRef: MatDialogRef<EditActuationTypeDialogComponent>,
        private fb: UntypedFormBuilder,
        private currencyService: CurrenyService,
        @Inject(MAT_DIALOG_DATA) private data: EditActuationDialogData
    ) {}

    ngOnInit(): void {
        this.initForm();
        this.initData();
    }

    //#region INIT

    /**
     * Init form
     */
    private initForm(): void {
        this.form = this.fb.group({
            tipo: [null, Validators.required],
            importeMedico: [null, [Validators.min(0)]],
        });
    }

    /**
     * Get data from API and set form and other data. This is an async method to load data from API
     * and set them into relevant things, exception handling and etc... Basically like the main method
     * I just do not want to waste ngOnInit() function
     */
    private async initData(): Promise<void> {
        setTimeout(() => this.pageLoader.ShowPageLoader(), 0);

        const types = await this.getActionTypes();
        const action = await this.getActuation(this.data.actuationId);

        setTimeout(() => this.pageLoader.HidePageLoader(), 0);

        if (!types || !action) {
            this.sharedService.ShowSnackBar(
                'Lo siento, la carga de datos ha fallado'
            );
            this.close();
            return;
        }

        this.actionTypes = types;
        this.actionTypes.sort((a, b) => a.viewValue.localeCompare(b.viewValue));
        this.fillForm(action);
    }

    //#endregion

    //#region EVENTS

    /**
     * Close dialog event handler
     */
    public close() {
        this.dialogRef.close(false);
    }

    /**
     * Save button event handler
     */
    public async save() {
        this.form.markAllAsTouched();
        if (!this.form.valid) return;

        const action = this.convertFromToActuation(this.form);

        this.pageLoader.ShowPageLoader();
        const updateActuation = await this.changeActionType(
            this.data.actuationId,
            action
        );
        this.pageLoader.HidePageLoader();

        if (!updateActuation) {
            this.sharedService.ShowSnackBar(
                'Lo siento, el cambio de tipo de acción ha fallado'
            );
        } else {
            this.sharedService.ShowSnackBar(
                'Tipo de acción cambiado correctamente'
            );
            this.dialogRef.close(true);
        }
    }

    //#endregion

    //#region MECHANICS

    /**
     * Get action types
     */
    private async getActionTypes(): Promise<DropDownOption<number>[]> {
        let types: DropDownOption<number>[] = null;

        try {
            const res = await this.actuationService
                .getActionsTypes()
                .toPromise();

            if (res.code === 0) {
                types = res.result;
            } else {
                console.error('Server Error: ' + res.message);
            }
        } catch (error) {
            console.error(error);
        }

        return types;
    }

    /**
     * Get actuation
     */
    private async getActuation(id: number): Promise<Actuation> {
        let actuation: Actuation = null;

        try {
            actuation = await ApiCallsHelper.toPromise(
                this.actuationService.getActuation(id)
            );
        } catch (error) {
            console.error(error);
        }

        return actuation;
    }

    /**
     * Fill form with actuation data
     */
    private fillForm(actuation: Actuation): void {
        const doctorFee =
            actuation.importeMedico !== null
                ? this.currencyService.setSpanishCurreny(
                      actuation.importeMedico
                  )
                : null;
        this.form.patchValue({
            tipo: actuation.tipo,
            importeMedico: doctorFee,
        });
        this.form.updateValueAndValidity();
    }

    /**
     * Change action type
     */
    private async changeActionType(
        id: number,
        action: Actuation
    ): Promise<Actuation> {
        let updatedAction: Actuation = null;

        try {
            updatedAction = await ApiCallsHelper.toPromise(
                this.actuationService.changeActionType(id, action)
            );
        } catch (error) {
            console.error(error);
        }

        return updatedAction;
    }

    /**
     * Convert form data to actuation
     */
    private convertFromToActuation(form: UntypedFormGroup) {
        const action = form.value as Actuation;
        action.tipoActuacion = this.actionTypes.find(
            (x) => x.value === action.tipo
        ).viewValue;
        action.importeMedico = Boolean(this.form.value.importeMedico)
            ? this.currencyService.replaceSpanishFormat(
                  this.form.value.importeMedico
              )
            : null;
        return action;
    }

    //#endregion
}
